import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  Plus, FileText, Upload, Calendar, Users, MoreVertical,
  Trash2, Edit2, Eye, Download, Link2, Clock, CheckCircle2,
  AlertTriangle, Target, Building2, Rocket
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import SectionHeader from '../components/shared/SectionHeader';
import HeaderGlow from '../components/HeaderGlow';
import { useOrg } from '../context/OrgContext';
import { mockStrategies, strategySections } from '../mock/strategyDevData';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Set to true to use mock data, false to use API
const USE_MOCK_DATA = false;

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  archived: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const statusLabels = {
  draft: 'Draft',
  in_progress: 'In Progress',
  review: 'In Review',
  approved: 'Approved',
  archived: 'Archived'
};

const StrategyDevelopment = ({ autoCreate = false }) => {
  const navigate = useNavigate();
  const { selectedEntity } = useOrg();
  const [strategies, setStrategies] = useState([]);
  const [sectionsConfig, setSectionsConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(autoCreate);
  const [newStrategy, setNewStrategy] = useState({
    name: '',
    description: '',
    year: new Date().getFullYear() + 1,
    duration: 3,
    import_mode: false
  });

  // Derive context labels from selected entity
  const entityLabel = selectedEntity?.name || 'All';
  const isFunction = selectedEntity?.type === 'function';
  const isAllView = !selectedEntity;
  const headerTitle = isAllView ? 'Strategy Development' : `${entityLabel} Strategy`;
  const headerSubtitle = isAllView
    ? 'AI-Guided Strategic Planning Journey'
    : `${entityLabel} - AI-Guided Strategic Planning Journey`;
  const strategiesTitle = isAllView ? 'Strategies' : `${entityLabel} Strategies`;

  // Fetch strategies and config
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (USE_MOCK_DATA) {
        // Use mock data
        setStrategies(mockStrategies);
        setSectionsConfig(strategySections);
      } else {
        // Use API
        const [strategiesRes, configRes] = await Promise.all([
          axios.get(`${API_URL}/api/strategy-dev/projects`),
          axios.get(`${API_URL}/api/strategy-dev/sections-config`)
        ]);
        setStrategies(strategiesRes.data);
        setSectionsConfig(configRes.data.sections);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate completion percentage
  const calculateProgress = (strategy) => {
    if (!strategy.sections || strategy.sections.length === 0) return 0;
    const completedSections = strategy.sections.filter(
      s => s.status === 'reviewed' || s.status === 'approved'
    ).length;
    return Math.round((completedSections / strategy.sections.length) * 100);
  };

  // Filter strategies by selected entity (null = show all)
  const filteredStrategies = selectedEntity
    ? strategies.filter(s => s.entity_id === selectedEntity.id)
    : strategies;

  // Create new strategy
  const handleCreateStrategy = async () => {
    if (!newStrategy.name.trim()) {
      toast.error('Please enter a strategy name');
      return;
    }

    const strategyPayload = {
      ...newStrategy,
      entity_id: selectedEntity?.id || 'corporate',
      entity_name: selectedEntity?.name || '',
      entity_type: selectedEntity?.type || 'corporate'
    };

    try {
      if (USE_MOCK_DATA) {
        // Create mock strategy
        const mockNewStrategy = {
          id: `strategy-${Date.now()}`,
          workspace_id: 'default',
          name: newStrategy.name,
          description: newStrategy.description,
          year: newStrategy.year,
          status: 'draft',
          sections: strategySections.map(s => ({
            section_id: s.id,
            status: 'not_started',
            data: {},
            ai_suggestions: [],
            notes: ''
          })),
          created_by: 'user-1',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          collaborators: [{
            user_id: 'user-1',
            user_name: 'Test User',
            user_email: 'testuser@example.com',
            role: 'owner',
            added_at: new Date().toISOString()
          }],
          linked_to_execution: false
        };
        
        toast.success('Strategy created successfully');
        setStrategies([mockNewStrategy, ...strategies]);
        setIsCreateDialogOpen(false);
        setNewStrategy({ name: '', description: '', year: new Date().getFullYear() + 1, import_mode: false });
        navigate(`/strategy-development/${mockNewStrategy.id}`);
      } else {
        const response = await axios.post(`${API_URL}/api/strategy-dev/projects`, strategyPayload);
        toast.success('Strategy created successfully');
        setStrategies([response.data, ...strategies]);
        setIsCreateDialogOpen(false);
        setNewStrategy({ name: '', description: '', year: new Date().getFullYear() + 1, import_mode: false });
        navigate(`/strategy-development/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error creating strategy:', error);
      toast.error('Failed to create strategy');
    }
  };

  // Delete strategy
  const handleDeleteStrategy = async (strategyId) => {
    if (!window.confirm('Are you sure you want to delete this strategy?')) return;

    try {
      if (USE_MOCK_DATA) {
        setStrategies(strategies.filter(s => s.id !== strategyId));
        toast.success('Strategy deleted');
      } else {
        await axios.delete(`${API_URL}/api/strategy-dev/projects/${strategyId}`);
        toast.success('Strategy deleted');
        setStrategies(strategies.filter(s => s.id !== strategyId));
      }
    } catch (error) {
      console.error('Error deleting strategy:', error);
      toast.error('Failed to delete strategy');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600/20 via-sky-500/20 to-blue-400/20 border-b border-border">
        <div className="px-6 py-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-sky-500 rounded-xl">
              <Target className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{headerTitle}</h1>
              <p className="text-muted-foreground">{headerSubtitle}</p>
            </div>
          </div>
        </div>
        <HeaderGlow />
      </div>

      <div className="p-6 space-y-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card 
            className="bg-gradient-to-br from-cyan-500/10 to-sky-500/10 border-cyan-500/20 cursor-pointer hover:border-cyan-500/40 transition-colors"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-4 bg-cyan-500/20 rounded-xl">
                <Rocket className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Start New Strategy</h3>
                <p className="text-sm text-muted-foreground">Begin an AI-guided strategic planning journey from scratch</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-sky-500/10 to-blue-500/10 border-sky-500/20 cursor-pointer hover:border-sky-500/40 transition-colors"
            onClick={() => {
              setNewStrategy({ ...newStrategy, import_mode: true });
              setIsCreateDialogOpen(true);
            }}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-4 bg-sky-500/20 rounded-xl">
                <Upload className="w-8 h-8 text-sky-400" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Import & Refine</h3>
                <p className="text-sm text-muted-foreground">Upload existing strategy document and refine with AI</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Strategies List */}
        <SectionHeader
          title={strategiesTitle}
          icon={FileText}
          iconColor="text-purple-500"
          badge={<Badge variant="secondary">{filteredStrategies.length} {filteredStrategies.length === 1 ? 'strategy' : 'strategies'}</Badge>}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredStrategies.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No strategies yet</h3>
              <p className="text-muted-foreground mb-4">Create your first strategy to get started</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Strategy
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredStrategies.map(strategy => (
                <Card 
                  key={strategy.id} 
                  className="bg-card/50 border-border hover:border-primary/50 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/strategy-development/${strategy.id}`)}
                  data-testid={`strategy-card-${strategy.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Badge className={`${statusColors[strategy.status]} mb-2`}>
                          {statusLabels[strategy.status]}
                        </Badge>
                        <CardTitle className="text-lg text-foreground group-hover:text-primary transition-colors">
                          {strategy.name}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {strategy.year} {strategy.entity_name || strategy.entity_type || 'Corporate'} Strategy
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/strategy-development/${strategy.id}`); }}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); }}>
                            <Download className="w-4 h-4 mr-2" />
                            Export
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDeleteStrategy(strategy.id); }}
                            className="text-red-500 focus:text-red-500"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {strategy.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {strategy.description}
                      </p>
                    )}
                    
                    {/* Progress */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Completion</span>
                        <span className="font-medium text-foreground">{calculateProgress(strategy)}%</span>
                      </div>
                      <Progress value={calculateProgress(strategy)} className="h-2" />
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span>{strategy.collaborators?.length || 1} collaborators</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Updated {new Date(strategy.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Linked indicator */}
                    {strategy.linked_to_execution && (
                      <div className="flex items-center gap-1 text-xs text-green-500 mt-2">
                        <Link2 className="w-3 h-3" />
                        <span>Linked to Execution</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </SectionHeader>
      </div>

      {/* Create Strategy Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          {/* Entity Header */}
          {selectedEntity && (
            <div className="bg-gradient-to-r from-cyan-600/20 via-sky-500/20 to-blue-400/20 -mx-6 -mt-6 mb-4 px-6 py-4 rounded-t-lg border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <Building2 className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{selectedEntity.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{selectedEntity.type} Strategy</p>
                </div>
              </div>
            </div>
          )}
          {!selectedEntity && (
            <div className="bg-gradient-to-r from-cyan-600/20 via-sky-500/20 to-blue-400/20 -mx-6 -mt-6 mb-4 px-6 py-4 rounded-t-lg border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <Target className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">New Strategy</p>
                  <p className="text-xs text-muted-foreground">Select an entity from the sidebar to create entity-specific strategy</p>
                </div>
              </div>
            </div>
          )}

          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {newStrategy.import_mode ? (
                <>
                  <Upload className="w-5 h-5 text-blue-500" />
                  Import Strategy
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5 text-purple-500" />
                  New Strategy
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Strategy Name</Label>
              <Input
                id="name"
                placeholder="e.g., 2026 Corporate Strategy"
                value={newStrategy.name}
                onChange={e => setNewStrategy({ ...newStrategy, name: e.target.value })}
                data-testid="strategy-name-input"
              />
            </div>
            
            {/* Duration + Start Year */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Strategy Duration</Label>
                <Select
                  value={String(newStrategy.duration)}
                  onValueChange={value => setNewStrategy({ ...newStrategy, duration: parseInt(value) })}
                >
                  <SelectTrigger data-testid="duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Year</SelectItem>
                    <SelectItem value="2">2 Years</SelectItem>
                    <SelectItem value="3">3 Years</SelectItem>
                    <SelectItem value="5">5 Years</SelectItem>
                    <SelectItem value="7">7 Years</SelectItem>
                    <SelectItem value="10">10 Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Year</Label>
                <Select
                  value={String(newStrategy.year)}
                  onValueChange={value => setNewStrategy({ ...newStrategy, year: parseInt(value) })}
                >
                  <SelectTrigger data-testid="start-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() + i;
                      return (
                        <SelectItem key={year} value={String(year)}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this strategy..."
                value={newStrategy.description}
                onChange={e => setNewStrategy({ ...newStrategy, description: e.target.value })}
                rows={2}
              />
            </div>

            {newStrategy.import_mode && (
              <div className="p-4 border-2 border-dashed border-border rounded-lg text-center">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Upload your existing strategy document (PPTX, DOCX, PDF)
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Coming soon - for now, start fresh and use AI assistance
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateStrategy} data-testid="create-strategy-submit">
              {newStrategy.import_mode ? 'Import & Continue' : 'Create Strategy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategyDevelopment;
